import React, { useState, useEffect } from "react";
import { 
    Box, Typography, Grid, Paper, Chip, CircularProgress, 
    Fade, Avatar, LinearProgress, Divider, Container, Button 
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faHandHoldingHeart, faBoxOpen, faMapMarkerAlt, 
    faClock, faShieldHeart, faCheckCircle, 
    faTruck
} from "@fortawesome/free-solid-svg-icons";
import { getTransparencyData } from "../../../controllers/help/HelpController";

export default function HelpTransparencyView() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ metrics: null, feed: [], dispatches: [], dispatch_metrics: null });
    const [tab, setTab] = useState('social');
    const [error, setError] = useState(null);

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const response = await getTransparencyData();
            setData(response.data); 
        } catch (err) {
            setError("No se pudieron cargar los datos de transparencia.");
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <LoadingScreen />;
    if (error) return <ErrorScreen message={error} />;

    const { metrics, feed, dispatches, dispatch_metrics } = data || {};
    if (!metrics) return <ErrorScreen message="No hay datos disponibles." />;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8', py: 8 }}>
            <Container maxWidth='xl'>
                {/* CABECERA */}
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Avatar sx={{ bgcolor: 'rgba(46, 125, 50, 0.1)', color: '#2e7d32', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                        <FontAwesomeIcon icon={faShieldHeart} size="lg" />
                    </Avatar>
                    <Typography variant="h3" sx={{ fontWeight: 900, color: '#1a1a1a', letterSpacing: -1, mb: 1.5 }}>
                        Auditoría Social en Tiempo Real
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, maxWidth: 1000, mx: 'auto' }}>
                        Nuestra prioridad es la transparencia. Aquí puedes monitorear el impacto de las donaciones y la entrega de insumos a las familias afectadas, protegiendo siempre su identidad.
                    </Typography>
                </Box>

                <TabSelector currentTab={tab} setTab={setTab} />

                {tab === 'social' ? (
                    <SocialAuditView metrics={metrics} feed={feed} />
                ) : (
                    <LogisticsAuditView dispatches={dispatches} metrics={dispatch_metrics} />
                )}
            </Container>
            <style>{`@keyframes pulse { 0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.7); } 70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(211, 47, 47, 0); } 100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(211, 47, 47, 0); } }`}</style>
        </Box>
    );
}

// --- SUB-COMPONENTES PARA AUDITABILIDAD ---

const Header = ({ title }) => (
    <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Avatar sx={{ bgcolor: 'rgba(46, 125, 50, 0.1)', color: '#2e7d32', width: 64, height: 64, mx: 'auto', mb: 2 }}>
            <FontAwesomeIcon icon={faShieldHeart} size="lg" />
        </Avatar>
        <Typography variant="h3" sx={{ fontWeight: 900, mb: 1.5 }}>{title}</Typography>
    </Box>
);

const TabSelector = ({ currentTab, setTab }) => (
    <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Paper elevation={0} sx={{ display: 'inline-flex', borderRadius: 3, p: 0.5, bgcolor: '#eee' }}>
            <Button onClick={() => setTab('social')} sx={{ bgcolor: currentTab === 'social' ? '#fff' : 'transparent', color: '#000', borderRadius: 2, px: 4, fontWeight: 800 }}>Auditoría Social</Button>
            <Button onClick={() => setTab('logistica')} sx={{ bgcolor: currentTab === 'logistica' ? '#fff' : 'transparent', color: '#000', borderRadius: 2, px: 4, fontWeight: 800 }}>Logística</Button>
        </Paper>
    </Box>
);

const SocialAuditView = ({ metrics, feed }) => {
    const maxCityValue = Math.max(...Object.values(metrics.by_city || {}));
    return (
        <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: 4, mb: 4, bgcolor: '#FFF', color: '#fff', textAlign: 'center' }}>
                    <Typography variant="h2" sx={{ fontWeight: 900, color: '#4caf50' }}>{metrics.total_delivered > 0 ? metrics.total_delivered : metrics.total_received}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>FAMILIAS ASISTIDAS</Typography>
                </Paper>
                <Paper elevation={0} sx={{ p: 4, borderRadius: 4, mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box><Typography variant="h4" sx={{ fontWeight: 800 }}>{metrics.total_pending}</Typography><Typography variant="body2" color="text.secondary">CASOS EN EVALUACIÓN</Typography></Box>
                    <Avatar sx={{ bgcolor: '#fff3e0', color: '#f57c00' }}><FontAwesomeIcon icon={faBoxOpen} /></Avatar>
                </Paper>
            </Grid>
            <Grid item xs={12} md={7}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 3 }}>Últimas Entregas</Typography>
                    {feed.map((item, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 2, mb: 3 }}>
                            <Avatar sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }}><FontAwesomeIcon icon={faCheckCircle} size="sm" /></Avatar>
                            <Box><Typography sx={{ fontWeight: 700 }}>{item.family_size} personas asistidas</Typography><Typography variant="caption">{item.city} • {item.time_ago}</Typography></Box>
                        </Box>
                    ))}
                </Paper>
            </Grid>
        </Grid>
    );
};

const LogisticsAuditView = ({ dispatches, metrics }) => (
    <Grid container spacing={4}>
        {/* COLUMNA IZQUIERDA: MÉTRICAS LOGÍSTICAS */}
        <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Resumen Operativo</Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }}>
                        <FontAwesomeIcon icon={faTruck} />
                    </Avatar>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 900 }}>{metrics?.total_dispatches || 0}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Total Envíos</Typography>
                    </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }}>
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </Avatar>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 900 }}>{metrics?.delivered_count || 0}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Completados</Typography>
                    </Box>
                </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>Distribución por Vehículo</Typography>
                {metrics?.by_category?.length > 0 ? (
                    metrics.by_category.map((cat) => (
                        <Box key={cat.vehicle_category} sx={{ mb: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>{cat.vehicle_category}</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>{cat.total}</Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={(cat.total / (metrics.total_dispatches || 1)) * 100} 
                                sx={{ height: 6, borderRadius: 3, bgcolor: '#f0f0f0', '& .MuiLinearProgress-bar': { bgcolor: '#1565c0' } }} 
                            />
                        </Box>
                    ))
                ) : (
                    <Typography variant="caption" color="text.secondary">No hay datos por categoría.</Typography>
                )}
            </Paper>
        </Grid>
        
        {/* COLUMNA DERECHA: LISTADO DE DESPACHOS */}
        <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {dispatches && dispatches.length > 0 ? (
                    dispatches.map((d) => (
                        <Paper key={d.id} elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography sx={{ fontWeight: 900, color: '#1565c0' }}>{d.tracking_code}</Typography>
                                <Chip 
                                    label={d.status.toUpperCase()} 
                                    color={d.status === 'entregado' ? 'success' : 'warning'} 
                                    size="small" 
                                    sx={{ fontWeight: 800 }}
                                />
                            </Box>
                            <Typography sx={{ fontWeight: 700, mb: 1 }}>Destino: {d.destination_city}</Typography>
                            
                            {/* Renderizado de React Quill (HTML seguro) */}
                            <Box 
                                sx={{ mt: 1, p: 2, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px solid #eee', fontSize: '0.9rem' }} 
                                dangerouslySetInnerHTML={{ __html: d.cargo_description }} 
                            />
                            
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Typography variant="caption" sx={{ color: '#888', fontWeight: 600 }}>
                                    <FontAwesomeIcon icon={faClock} style={{ marginRight: 4 }} /> {d.time_ago}
                                </Typography>
                            </Box>
                        </Paper>
                    ))
                ) : (
                    <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 4 }}>
                        <Typography color="text.secondary">No hay despachos registrados para mostrar.</Typography>
                    </Paper>
                )}
            </Box>
        </Grid>
    </Grid>
);

const LoadingScreen = () => <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
const ErrorScreen = ({ message }) => <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><Typography color="error">{message}</Typography></Box>;